import CubeIcon from '@heroicons/react/solid/CubeIcon'
import GlobeAltIcon from '@heroicons/react/solid/GlobeAltIcon'
import KeyIcon from '@heroicons/react/solid/KeyIcon'
import MailIcon from '@heroicons/react/solid/MailIcon'
import PencilAltIcon from '@heroicons/react/solid/PencilAltIcon'
import QuestionMarkCircleIcon from '@heroicons/react/solid/QuestionMarkCircleIcon'
import ShieldCheckIcon from '@heroicons/react/solid/ShieldCheckIcon'
import TagIcon from '@heroicons/react/solid/TagIcon'
import ThumbUpIcon from '@heroicons/react/solid/ThumbUpIcon'
import UserAddIcon from '@heroicons/react/solid/UserAddIcon'
import ViewGridIcon from '@heroicons/react/solid/ViewGridIcon'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Link as RouterLink } from 'react-router-dom'

import { Member, Post } from '@tribeplatform/gql-client/types'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Link } from '@tribeplatform/react-ui-kit/Link'

export const parseLog = log => {
  const payload = log.payload.reduce((props, keyValue) => {
    try {
      props[keyValue.key] = JSON.parse(keyValue?.value)
    } catch (err) {
      logger.error('Cannot parse activity log value')
    }
    return props
  }, {})

  return {
    payload: { ...payload },
    entities: log.entities,
  }
}

export const getLogIcon = noun => {
  const mapping = {
    SPACE: { icon: CubeIcon, color: 'bg-purple-500' },
    SPACE_MEMBERSHIP: { icon: CubeIcon, color: 'bg-purple-500' },
    SPACE_JOIN_REQUEST: { icon: CubeIcon, color: 'bg-purple-500' },
    POST: { icon: PencilAltIcon, color: 'bg-green-500' },
    MEMBER: { icon: UserAddIcon, color: 'bg-blue-500' },
    NETWORK: { icon: GlobeAltIcon, color: 'bg-gray-500' },
    COLLECTION: { icon: ViewGridIcon, color: 'bg-purple-500' },
    REACTION: { icon: ThumbUpIcon, color: 'bg-yellow-500' },
    MODERATION: { icon: ShieldCheckIcon, color: 'bg-red-500' },
    MEMBER_INVITATION: { icon: MailIcon, color: 'bg-blue-500' },
    APP_INSTALLATION: { icon: ViewGridIcon, color: 'bg-indigo-500' },
    TAG: { icon: TagIcon, color: 'bg-indigo-500' },
    APP: { icon: ViewGridIcon, color: 'bg-indigo-500' },
    SSO: { icon: KeyIcon, color: 'bg-gray-500' },
  }
  return mapping[noun] || { icon: QuestionMarkCircleIcon, color: 'bg-gray-500' }
}

export const getLogMemberLink = (member: Member) => {
  if (member?.id)
    return (
      <Link as={RouterLink} to={`/member/${member?.id}`}>
        {member?.name}
      </Link>
    )
  return <span>Someone</span>
}

export const getLogPostLink = (post: Post) => {
  if (post?.repliedToIds?.length) {
    return (
      <Link
        as={RouterLink}
        to={`/${post?.space?.slug}/post/${
          post?.repliedToIds[post?.repliedToIds.length - 1]
        }`}
      >
        reply
      </Link>
    )
  }
  return (
    <Link
      as={RouterLink}
      to={`/${post?.space?.slug}/post/${post?.slug}-${post?.id}`}
    >
      {post?.title || 'a post'}
    </Link>
  )
}

export const AuditLogRow = ({ log: { payload, entities } }) => {
  const noun = payload?.noun
  const verb = payload?.verb

  if (
    !noun ||
    !verb ||
    ['SPACE_NETWORK_ROLE', 'ROLE', 'SPACE_ROLE'].indexOf(noun) !== -1
  )
    return null

  const action = `${noun}.${verb}`
  const { icon: Icon, color: iconColor } = getLogIcon(noun)
  const { actor, member, app, space, post } = entities
  const object = payload?.object
  let actorLink = getLogMemberLink(actor)
  const spaceLink = space?.slug ? (
    <Link href={`/${space?.slug}`}>{space?.name}</Link>
  ) : (
    'a space'
  )
  const memberLink = getLogMemberLink(member)
  const postLink = getLogPostLink(post)

  let sentence = (
    <>
      {actorLink} {verb} {noun}
    </>
  )
  switch (action) {
    case 'APP_INSTALLATION.CREATED':
      sentence = (
        <>
          {actorLink} installed {app.name || 'an'} app
        </>
      )
      break

    case 'APP_INSTALLATION.DELETED':
      sentence = (
        <>
          {actorLink} uninstalled {app.name || 'an'} app
        </>
      )
      break

    case 'APP.CREATED':
      sentence = (
        <>
          {actorLink} created {object?.name} app
        </>
      )
      break

    case 'APP.UPDATED':
      sentence = (
        <>
          {actorLink} updated {object?.name} app settings
        </>
      )
      break

    case 'MEMBER.CREATED':
      actorLink = getLogMemberLink(object)
      sentence = <>{actorLink} joined the community</>
      break

    case 'MEMBER.UPDATED':
      if (object.id === actor?.id)
        sentence = <>{actorLink} updated their profile</>
      else
        sentence = (
          <>
            {actorLink} updated {memberLink}&apos; profile
          </>
        )
      break

    case 'MEMBER.VERIFIED':
      sentence = <>{actorLink} verified their account</>
      break

    case 'MEMBER_INVITATION.CREATED':
      sentence = (
        <>
          {actorLink} sent invitation to {object?.inviteeEmail}
        </>
      )
      break

    case 'MEMBER_INVITATION.UPDATED':
      sentence = <>{actorLink} re-sent a member invitation</>
      break

    case 'MODERATION.CREATED':
      if (object.entityType === 'POST') {
        sentence = (
          <>
            {actorLink}&apos;s {postLink} was flagged for moderation
          </>
        )
      }
      break

    case 'SSO.CREATED':
      sentence = <>{actorLink} enabled SSO</>
      break

    case 'TAG.ADDED':
      sentence = (
        <>
          {actorLink} added {object?.title} tag
        </>
      )
      break

    case 'TAG.REMOVED':
      sentence = (
        <>
          {actorLink} removed {object?.title} tag
        </>
      )
      break

    case 'MODERATION.REJECTED':
      if (object.entityType === 'POST')
        sentence = (
          <>
            {actorLink} approved {postLink}
          </>
        )
      break

    case 'MODERATION.ACCEPTED':
      if (object.entityType === 'POST')
        sentence = (
          <>
            {actorLink} rejected {postLink}
          </>
        )
      break

    case 'NETWORK.CREATED':
      sentence = <>{actorLink} created this community</>
      break

    case 'NETWORK.UPDATED':
      sentence = <>{actorLink} updated community settings</>
      break

    case 'POST.CREATED':
    case 'POST.BLOCKED':
    case 'POST.UNBLOCKED':
    case 'CUSTOM.CREATED':
      return null

    case 'POST.FOLLOWED':
      sentence = (
        <>
          {actorLink} followed {postLink}
        </>
      )
      break

    case 'POST.UNFOLLOWED':
      sentence = (
        <>
          {actorLink} unfollowed {postLink}
        </>
      )
      break

    case 'POST.HIDDEN':
      sentence = (
        <>
          {actorLink} hid <Link>{object?.title}</Link>
        </>
      )
      break

    case 'POST.UNHIDDEN':
      sentence = (
        <>
          {actorLink} unhid <Link>{object?.title}</Link>
        </>
      )
      break

    case 'POST.PUBLISHED':
      if (object?.repliedToId)
        sentence = (
          <>
            {actorLink} replied to {postLink}
          </>
        )
      else
        sentence = (
          <>
            {actorLink} posted {postLink}
          </>
        )
      break

    case 'POST.UNPUBLISHED':
      sentence = (
        <>
          {actorLink} deleted <Link>{object?.title}</Link> post
        </>
      )

      break
    case 'POST.DELETED':
      return null

    case 'POST.UPDATED':
      sentence = (
        <>
          {actorLink} updated {postLink}
        </>
      )
      break
    case 'REACTION.ADDED':
      sentence = (
        <>
          {actorLink} added reaction to {postLink}
        </>
      )
      break

    case 'REACTION.REMOVED':
      sentence = (
        <>
          {actorLink} removed reaction from {postLink}
        </>
      )
      break
    case 'SPACE.CREATED':
      sentence = (
        <>
          {actorLink} created {spaceLink}
        </>
      )
      break

    case 'SPACE.DELETED':
      sentence = (
        <>
          {actorLink} deleted {object?.name} space
        </>
      )
      break

    case 'SPACE.UPDATED':
      sentence = (
        <>
          {actorLink} updated {spaceLink}
        </>
      )
      break

    case 'COLLECTION.CREATED':
      sentence = (
        <>
          {actorLink} created{' '}
          <Link href={`/collection/${object?.id}`}>{object?.name}</Link>{' '}
          collection
        </>
      )
      break

    case 'COLLECTION.DELETED':
      sentence = (
        <>
          {actorLink} deleted {object?.name} collection
        </>
      )
      break

    case 'COLLECTION.UPDATED':
      sentence = (
        <>
          {actorLink} updated{' '}
          <Link href={`/collection/${object?.id}`}>{object?.name}</Link>{' '}
          collection
        </>
      )
      break

    case 'SPACE_MEMBERSHIP.CREATED':
      if (member?.id === actor?.id) {
        sentence = (
          <>
            {actorLink} joined {spaceLink}
          </>
        )
      } else {
        sentence = (
          <>
            {actorLink} added {memberLink} to {spaceLink}
          </>
        )
      }
      break

    case 'SPACE_MEMBERSHIP.DELETED':
      if (member?.id === actor?.id) {
        sentence = (
          <>
            {actorLink} left {spaceLink}
          </>
        )
      } else {
        sentence = (
          <>
            {actorLink} removed {memberLink} from {spaceLink}
          </>
        )
      }
      break

    case 'SPACE_JOIN_REQUEST.CREATED':
      sentence = (
        <>
          {actorLink} requested to join {spaceLink}
        </>
      )
      break

    case 'SPACE_JOIN_REQUEST.ACCEPTED':
      sentence = (
        <>
          {actorLink} accepted {memberLink}&apos;s join request to {spaceLink}
        </>
      )
      break

    case 'SSO.UPDATED':
      sentence = <>{actorLink} updated community SSO settings</>
      break

    default:
  }

  if (!sentence) return null

  return (
    <List.Item className="flex items-center space-x-3">
      <div className={clsx('rounded-full text-surface-50 p-2', iconColor)}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div>{sentence}</div>
        <time
          dateTime={payload.createdAt}
          title={payload.createdAt}
          className="text-sm"
        >
          {dayjs(payload.createdAt).fromNow()}
        </time>
      </div>
    </List.Item>
  )
}
